import { useEffect, useRef, useState } from "react";

export interface DropdownOption {
  value: string | number;
  label: string;
}

interface DropdownProps {
  value: string | number | null;
  options: DropdownOption[];
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
}

export function Dropdown({
  value,
  options,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  loading = false,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the selected option's label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.label || placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when pressing Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string | number | null) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown trigger button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 text-left
          border border-border-medium rounded-lg
          bg-white/70 backdrop-blur-sm
          text-text-primary text-sm font-medium font-sans
          focus:border-action-key-default focus:ring-1 focus:ring-action-key-default focus:outline-none
          transition-all duration-200
          ${disabled || loading ? "opacity-50 cursor-not-allowed" : "hover:shadow-md cursor-pointer"}
          ${isOpen ? "border-action-key-default ring-1 ring-action-key-default" : ""}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={value === null ? "text-text-muted" : ""}>
            {displayValue}
          </span>
          <div className="flex items-center">
            {loading && (
              <div className="mr-2">
                <div className="w-4 h-4 border-2 border-action-key-default border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <svg
              className={`w-4 h-4 text-text-muted transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && !disabled && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border-medium rounded-lg shadow-lg overflow-hidden">
          <div className="max-h-60 overflow-auto">
            {/* Placeholder/All option */}
            {placeholder && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={`
                  w-full px-3 py-2 text-left text-sm font-sans
                  transition-colors duration-150
                  ${
                    value === null
                      ? "bg-action-key-lighter text-action-key-darker font-medium"
                      : "text-text-primary hover:bg-bg-surface"
                  }
                `}
              >
                {placeholder}
              </button>
            )}

            {/* Options */}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`
                  w-full px-3 py-2 text-left text-sm font-sans
                  transition-colors duration-150
                  ${
                    value === option.value
                      ? "bg-action-key-lighter text-action-key-darker font-medium"
                      : "text-text-primary hover:bg-bg-surface"
                  }
                `}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
